import { Injectable, Injector } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { SnackBarService } from "../../@ksm-core/shared/components/snackbar/snack-bar.service";
import { AuthService } from "../../@tpl-auth/services/auth.service";
import { LanguageTranslateService } from "../../@ksm-core/shared/services/language-translate.service";
import { AppSettingConstant } from "../../@ksm-core/shared/constants/app-setting.constant";
import { LocalStorageConstant } from "../../@tpl-auth/constants/local-storage.constant";
import { ErrorConstant } from "../../@ksm-core/shared/constants/error.constant";

@Injectable()
export class ResponseHandlerInterceptor implements HttpInterceptor {
  private get accessToken(): string | null {
    return localStorage.getItem(LocalStorageConstant.accessToken);
  }

  constructor(
    private injector: Injector,
    private languageTranslateService: LanguageTranslateService,
    private snackBarService: SnackBarService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.shouldHandleInterceptor(request.url)) {
      const authService = this.injector.get(AuthService);
      if (!this.accessToken) {
        authService.navigateLoginPage();
        this.snackBarService.warning(ErrorConstant.sessionExpire);
        return EMPTY;
      } else {
        request = this.addAuthorizationHeader(request);
      }
    }

    return next
      .handle(request)
      .pipe(catchError((error) => this.handleError(error)));
  }

  private shouldHandleInterceptor(requestUrl: string): boolean {
    return !ErrorConstant.urlShouldNotContainsForInterceptor.some((url) =>
      requestUrl.includes(url)
    );
  }

  private addAuthorizationHeader(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: this.accessToken!,
      },
    });
  }

  private handleError(error: any): Observable<never> {
    if (error.url && error.url.includes(ErrorConstant.assetsI18n)) {
      this.handleLanguageTranslationError();
      return throwError({ message: ErrorConstant.languageNotAvailable });
    }

    if (error.status === 401) {
      const authService = this.injector.get(AuthService);
      if (this.accessToken) {
        authService.logout();
      } else {
        authService.navigateLoginPage();
      }
      this.snackBarService.warning(ErrorConstant.sessionExpire);
      return EMPTY;
    }

    if (error.status === 403) {
      this.snackBarService.error(ErrorConstant.noUserAccess);
      return EMPTY;
    }

    if (error.status === 0) {
      this.snackBarService.warning(
        ErrorConstant.internetConnectivityHasBeenLost
      );
      return EMPTY;
    }

    if (error.status == 400) {
      return throwError({
        message: JSON.stringify(error.error),
        Code: error.status,
      });
    }

    if (error?.error?.Message) {
      return throwError({
        message: error.error.Message,
        Code: error.error.Code,
      });
    }
    return throwError(error);
  }

  private handleLanguageTranslationError(): void {
    this.languageTranslateService.changeLanguage(
      AppSettingConstant.defaultLanguage
    );
    AppSettingConstant.languages.subscribe((languages) => {
      const selectedLanguage = languages.find(
        (language) => language.key === AppSettingConstant.defaultLanguage
      );
      AppSettingConstant.selectedLanguage.next(selectedLanguage);
    });
    this.snackBarService.warning(ErrorConstant.languageNotAvailable);
  }
}
